import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useDeleteExtractionRow } from "@/features/data-curation/hooks/useCuration";
import type { ExtractionRow } from "@/features/data-curation/types";
import { Trash2 } from "lucide-react";

type DeleteRowDialogProps = {
  datasetId: string;
  /** The row to delete; the dialog is open while it is set. */
  row: ExtractionRow | null;
  onClose: () => void;
};

export const DeleteRowDialog = ({ datasetId, row, onClose }: DeleteRowDialogProps) => {
  const deleteRow = useDeleteExtractionRow(datasetId);
  const close = () => {
    deleteRow.reset();
    onClose();
  };
  return (
    <Dialog
      open={row != null}
      onOpenChange={(open) => {
        if (!open && !deleteRow.isPending) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete row {row?.row_number}?</DialogTitle>
          <DialogDescription>{row?.row_description}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-zinc-600">
          The row and its cells leave the grid, so a new row can take its place.
          Its papers can show again under the grid, in Incoming. Only a restore
          of a saved checkpoint brings the row back.
        </p>
        {deleteRow.error && (
          <p className="rounded-[3px] bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {deleteRow.error.message}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={deleteRow.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            loading={deleteRow.isPending}
            aria-label={deleteRow.isPending ? "Deleting row" : undefined}
            onClick={() => row && deleteRow.mutate(row.id, { onSuccess: close })}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
